import type { FC } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Checkbox as MuiCheckbox,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LayersIcon from '@mui/icons-material/Layers';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import type { HypothesisCreativeEntry, HypothesisSection } from '../../types/hypothesis';
import type { HypothesisPack } from '../../hooks';

const EMPTY_PACKS: HypothesisPack[] = [];

interface HypothesisGroupCardProps {
  hypothesis: string;
  creatives: HypothesisCreativeEntry[];
  packs?: HypothesisPack[];
  section: HypothesisSection | null;
  showSectionSelector: boolean;
  selectedIds: string[];
  columns: GridColDef[];
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange?: (packIndex: number, packCreativeIds: string[]) => void;
  onSectionChange?: (hypothesis: string, section: HypothesisSection) => void;
}

const dataGridSx = {
  border: 'none',
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-columnHeaders': {
    borderTop: 1,
    borderColor: 'divider',
    bgcolor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'text.secondary',
  },
} as const;

const HypothesisGroupCardImpl: FC<HypothesisGroupCardProps> = ({
  hypothesis,
  creatives,
  packs = EMPTY_PACKS,
  section,
  showSectionSelector,
  selectedIds,
  columns,
  onSelectionChange,
  onPackSelectionChange,
  onSectionChange,
}) => {
  const [expanded, setExpanded] = useState(true);

  const groupCreativeIds = useMemo(
    () => creatives.map((c) => c.id),
    [creatives],
  );

  const totalCount =
    creatives.length +
    packs.reduce((sum, pack) => sum + pack.creatives.length, 0);

  const allSelected =
    selectedIds.length === creatives.length && creatives.length > 0;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = useCallback(() => {
    onSelectionChange(hypothesis, allSelected ? [] : groupCreativeIds);
  }, [allSelected, groupCreativeIds, hypothesis, onSelectionChange]);

  const handleRowSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      onSelectionChange(hypothesis, model.map(String));
    },
    [hypothesis, onSelectionChange],
  );

  const handlePackRowSelectionChange = useCallback(
    (packIndex: number, model: GridRowSelectionModel) => {
      if (!onPackSelectionChange) return;
      onPackSelectionChange(packIndex, model.map(String));
    },
    [onPackSelectionChange],
  );

  const hasPacks = packs.length > 0;
  const hasUnpacked = creatives.length > 0;

  const packLabels = useMemo(() => {
    const tagOccurrences = new Map<string, number>();
    for (const p of packs) {
      tagOccurrences.set(p.tag, (tagOccurrences.get(p.tag) ?? 0) + 1);
    }
    const ordinalByTag = new Map<string, number>();
    return packs.map((p) => {
      const sameTagTotal = tagOccurrences.get(p.tag) ?? 1;
      if (sameTagTotal <= 1) {
        return `Pack: ${p.tag} (${p.creatives.length})`;
      }
      const nextOrdinal = (ordinalByTag.get(p.tag) ?? 0) + 1;
      ordinalByTag.set(p.tag, nextOrdinal);
      return `Pack: ${p.tag} (${nextOrdinal}) (${p.creatives.length})`;
    });
  }, [packs]);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: .75,
          pr: 1.5,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MuiCheckbox
            size="medium"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            onClick={(e) => { e.stopPropagation(); }}
          />
          <IconButton
            size="small"
            color='primary'
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ cursor: 'pointer', color: 'primary.main' }}
            onClick={() => { setExpanded((prev) => !prev); }}
          >
            {hypothesis}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            (Creatives: {totalCount})
          </Typography>
        </Box>
        {showSectionSelector && onSectionChange && (
          <FormControl size="small" variant="outlined">
            <Select
              value={section ?? 'upcoming'}
              onChange={(e) => {
                onSectionChange(hypothesis, e.target.value as HypothesisSection);
              }}
              size="small"
              sx={{ height: 32, minWidth: 120, fontSize: '0.875rem' }}
            >
              <MenuItem value="this_week">This week</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      <Collapse in={expanded} unmountOnExit>
        {packs.map((pack, packIdx) => {
          const packIds = pack.creatives.map((c) => c.id);
          return (
            <Box key={`pack-${String(pack.packIndex)}`} sx={{ mt: 0.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <LayersIcon fontSize="medium" sx={{ color: 'text.primary' }} />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {packLabels[packIdx]}
                </Typography>
              </Box>
              <DataGrid
                rows={pack.creatives}
                columns={columns}
                checkboxSelection
                rowSelectionModel={packIds}
                onRowSelectionModelChange={(model) => {
                  handlePackRowSelectionChange(pack.packIndex, model);
                }}
                keepNonExistentRowsSelected
                hideFooterPagination
                hideFooter
                disableColumnMenu
                rowHeight={60}
                getRowId={(row: HypothesisCreativeEntry) => row.id}
                sx={
                  packIdx === 0
                    ? {...dataGridSx, '& .MuiDataGrid-columnHeaders': { borderTop: 'none' },}
                    : {
                        ...dataGridSx,
                        '& .MuiDataGrid-columnHeaders': { display: 'none', borderTop: 'none' },
                      }
                }
              />
            </Box>
          );
        })}
        {hasPacks && hasUnpacked && (
          <Box
            sx={{
              px: 1.5,
              pt: 2,
              pb: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.primary"
            >
              Creatives not in pack:
            </Typography>
          </Box>
        )}
        {hasUnpacked && (
          <DataGrid
            rows={creatives}
            columns={columns}
            checkboxSelection
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={handleRowSelectionChange}
            keepNonExistentRowsSelected
            hideFooterPagination
            hideFooter
            disableColumnMenu
            rowHeight={60}
            getRowId={(row: HypothesisCreativeEntry) => row.id}
            sx={
              hasPacks
                ? {
                    ...dataGridSx,
                    '& .MuiDataGrid-columnHeaders': { display: 'none' },
                  }
                : dataGridSx
            }
          />
        )}
      </Collapse>
    </Card>
  );
};

const HypothesisGroupCard = memo(HypothesisGroupCardImpl);

export default HypothesisGroupCard;
